/**
 * Builds and edits questionnaires: tax organizers and client intake forms. A form is
 * sections of questions; a question can appear only when an earlier answer matches,
 * and can add documents or follow-up questions to the client's checklist.
 * The server validates the result with the same rules the portal runs.
 */
import { FIELD_TYPES, SENSITIVE_TYPES, fillYear, forTaxYear, QUESTION_TYPES, visibleQuestions, type Answers, type Condition, type GroupField, type OrganizerDefinition, type Question, type Rule } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Copy, Eye, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { ask } from "@/components/dialogs";
import { Empty, ListSearch, Loading, matches, Section, ShowMore } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QuestionInput } from "@/pages/portal-tax";
import { useToast } from "@/lib/use-toast";
import { api, ApiError, errorMessage } from "@/lib/api";
import { hasPermission, type Me } from "@/lib/session";
import { cn } from "@/lib/utils";
import { plural } from "@/lib/format";

export type FormKind = "organizer" | "intake";
export type FormTemplate = { id: string; name: string; kind: FormKind; module: string; isActive: boolean; definition: OrganizerDefinition; updatedAt: string; openCopies: number };

type QType = (typeof QUESTION_TYPES)[number];
const TYPE_LABELS: Record<QType, string> = {
  yes_no: "Yes / No",
  choice: "Multiple choice",
  text: "Short answer",
  long_text: "Paragraph",
  number: "Number",
  money: "Dollar amount",
  date: "Date",
  count: "Count (how many)",
  group: "List (one entry per item)",
  ssn: "Social Security number",
  ein: "EIN (employer ID)",
  routing: "Bank routing number",
  account: "Bank account number",
};
const FIELD_LABELS: Record<GroupField["type"], string> = {
  text: "Short answer",
  number: "Number",
  money: "Dollar amount",
  date: "Date",
  choice: "Multiple choice",
  yes_no: "Yes / No",
  ssn: "Social Security number",
  ein: "EIN",
  routing: "Routing number",
  account: "Account number",
};

const select = "h-9 rounded-md border border-input bg-background px-2 text-sm";

/** Lowercase key from a label, unique among `taken`. */
function makeKey(label: string, taken: Set<string>, fallback = "q"): string {
  let base = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 30);
  if (!/^[a-z]/.test(base)) base = `${fallback}_${base}`.replace(/_+$/, "");
  let key = base;
  for (let n = 2; taken.has(key); n++) key = `${base}_${n}`;
  return key;
}

const allKeys = (def: OrganizerDefinition) => new Set(def.sections.flatMap((s) => [s.key, ...s.questions.map((q) => q.key)]));

/** The answer that triggers a checklist item, by question type. */
function defaultWhen(q: Question): Rule["when"] {
  if (q.type === "yes_no") return { op: "eq", value: true };
  if (q.type === "choice") return { op: "eq", value: q.options?.[0]?.value ?? "" };
  if (q.type === "count" || q.type === "group") return { op: "gt", value: 0 };
  return { op: "answered" };
}

function whenLabel(q: Question, w: Rule["when"]): string {
  if (q.type === "yes_no") return w.value === false ? "answered No" : "answered Yes";
  if (q.type === "choice") return `answered “${q.options?.find((o) => o.value === w.value)?.label ?? w.value}”`;
  if (q.type === "count") return "more than 0";
  if (q.type === "group") return "at least one entry";
  return "answered";
}

/** Conditions that can be offered for an earlier question. */
function conditionChoices(q: Question): Array<{ label: string; cond: Condition }> {
  if (q.type === "yes_no")
    return [
      { label: "is Yes", cond: { key: q.key, op: "eq", value: true } },
      { label: "is No", cond: { key: q.key, op: "eq", value: false } },
    ];
  if (q.type === "choice") return (q.options ?? []).map((o) => ({ label: `is “${o.label}”`, cond: { key: q.key, op: "eq", value: o.value } }));
  if (q.type === "count") return [{ label: "is more than 0", cond: { key: q.key, op: "gt", value: 0 } }];
  if (q.type === "group") return [{ label: "has at least one entry", cond: { key: q.key, op: "gt", value: 0 } }];
  return [{ label: "is answered", cond: { key: q.key, op: "answered" } }];
}

const sameCond = (a: Condition, b: Condition) => a.key === b.key && a.op === b.op && a.value === b.value;

function QuestionEditor({
  q,
  kind,
  earlier,
  onChange,
  onMove,
  onDelete,
  canMoveUp,
  canMoveDown,
}: {
  q: Question;
  kind: FormKind;
  earlier: Question[];
  onChange: (q: Question) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  const [open, setOpen] = useState(!q.label);
  const set = (patch: Partial<Question>) => onChange({ ...q, ...patch });
  const setType = (type: QType) => {
    const next: Question = {
      ...q,
      type,
      options: type === "choice" ? (q.options?.length ? q.options : [{ value: "option_1", label: "Option 1" }, { value: "option_2", label: "Option 2" }]) : undefined,
      fields: type === "group" ? (q.fields?.length ? q.fields : [{ key: "name", type: "text", label: "Name", required: true }]) : undefined,
      itemLabel: type === "group" ? (q.itemLabel ?? "Item") : undefined,
      titleField: type === "group" ? (q.titleField ?? "name") : undefined,
    };
    if (type !== "group") delete next.maxRows;
    // Checklist triggers depend on the type; reset them to the type's natural trigger.
    next.creates = q.creates.map((r) => ({ ...r, when: defaultWhen(next), perItem: type === "count" || type === "group" ? r.perItem : false }));
    onChange(next);
  };
  const choices = earlier.flatMap((e) => conditionChoices(e).map((c) => ({ ...c, label: `“${e.label || e.key}” ${c.label}` })));
  const condIndex = q.showIf.length === 1 ? choices.findIndex((c) => sameCond(c.cond, q.showIf[0]!)) : -1;
  // A single condition the earlier questions no longer offer (option removed, type changed, question moved).
  const stale = q.showIf.length === 1 && condIndex < 0;

  return (
    <li className="rounded-md border border-border bg-background">
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2 text-left" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          <span className={cn("truncate text-sm", !q.label && "italic text-muted-foreground")}>{q.label || "New question"}</span>
        </button>
        <span className="hidden rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground sm:inline">{TYPE_LABELS[q.type]}</span>
        {q.required && <span className="hidden text-[11px] text-muted-foreground sm:inline">Required</span>}
        {stale ? (
          <span className="text-[11px] font-medium text-destructive">Condition needs fixing</span>
        ) : (
          q.showIf.length > 0 && <span className="hidden text-[11px] text-primary sm:inline">Conditional</span>
        )}
        {q.creates.length > 0 && <span className="hidden text-[11px] text-primary sm:inline">{q.creates.length} checklist item{q.creates.length > 1 ? "s" : ""}</span>}
        <Button type="button" size="icon" variant="ghost" aria-label="Move question up" disabled={!canMoveUp} onClick={() => onMove(-1)}>
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button type="button" size="icon" variant="ghost" aria-label="Move question down" disabled={!canMoveDown} onClick={() => onMove(1)}>
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button type="button" size="icon" variant="ghost" aria-label="Delete question" onClick={onDelete}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {open && (
        <div className="space-y-4 border-t border-border px-4 py-4">
          <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
            <div className="space-y-1">
              <Label>Question</Label>
              <Input value={q.label} placeholder="e.g. Did you sell any investments this year?" onChange={(e) => set({ label: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Answer type</Label>
              <select className={cn(select, "w-full")} value={q.type} onChange={(e) => setType(e.target.value as QType)}>
                {QUESTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Help text (optional)</Label>
            <Input value={q.help ?? ""} placeholder="Guidance shown under the question" onChange={(e) => set({ help: e.target.value || undefined })} />
          </div>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={q.required} onChange={(e) => set({ required: e.target.checked })} /> Required
            </label>
            {/* Intake forms are sent once, so there is no earlier answer to carry forward. */}
            {kind === "organizer" && (
              <label className="flex items-center gap-2" title="Fill in last year's answer when this form is sent again">
                <input type="checkbox" checked={q.prefill} onChange={(e) => set({ prefill: e.target.checked })} /> Carry forward last year's answer
              </label>
            )}
          </div>

          {q.type === "choice" && (
            <div className="space-y-2">
              <Label>Options</Label>
              {(q.options ?? []).map((o, i) => (
                <div key={o.value} className="flex gap-2">
                  <Input value={o.label} onChange={(e) => set({ options: q.options!.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                  <Button type="button" variant="ghost" size="icon" aria-label="Remove option" disabled={(q.options?.length ?? 0) <= 2} onClick={() => {
                      const options = q.options!.filter((_, j) => j !== i);
                      // Checklist items tied to the removed answer move to the first remaining one.
                      set({ options, creates: q.creates.map((r) => (r.when.value === o.value ? { ...r, when: { op: "eq", value: options[0]!.value } } : r)) });
                    }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  const taken = new Set((q.options ?? []).map((o) => o.value));
                  let n = (q.options?.length ?? 0) + 1;
                  while (taken.has(`option_${n}`)) n++;
                  set({ options: [...(q.options ?? []), { value: `option_${n}`, label: `Option ${n}` }] });
                }}
              >
                <Plus className="h-4 w-4" /> Add option
              </Button>
            </div>
          )}

          {q.type === "group" && <GroupFieldsEditor q={q} set={set} />}

          <div className="space-y-1">
            <Label>Show this question</Label>
            {q.showIf.length > 1 ? (
              <p className="text-sm text-muted-foreground">Only when {q.showIf.length} earlier answers match (set in the original form).</p>
            ) : (
              <>
              <select
                className={cn(select, "w-full", stale && "border-destructive")}
                aria-invalid={stale || undefined}
                value={stale ? -2 : condIndex}
                onChange={(e) => {
                  const i = Number(e.target.value);
                  set({ showIf: i < 0 ? [] : [choices[i]!.cond] });
                }}
              >
                {stale && <option value={-2}>Choose again: the earlier answer it depended on changed</option>}
                <option value={-1}>Always</option>
                {choices.map((c, i) => (
                  <option key={i} value={i}>
                    Only when {c.label}
                  </option>
                ))}
              </select>
              {stale && <p className="text-xs text-destructive">The earlier question was changed or moved, so this condition no longer works. Pick when to show this question.</p>}
              </>
            )}
          </div>

          <div className="space-y-2">
            <Label>Add to the client's checklist</Label>
            {q.creates.length === 0 && <p className="text-xs text-muted-foreground">Nothing is added. Add a document or follow-up question the client must provide when they give a particular answer.</p>}
            {q.creates.map((r, i) => (
              <div key={i} className="space-y-2 rounded-md border border-border bg-secondary/40 p-3">
                <div className="grid gap-2 md:grid-cols-[1fr_3fr_auto]">
                  <select className={select} value={r.type} onChange={(e) => set({ creates: q.creates.map((x, j) => (j === i ? { ...x, type: e.target.value as Rule["type"] } : x)) })}>
                    <option value="document">Request a document</option>
                    <option value="question">Ask a follow-up question</option>
                  </select>
                  <Input placeholder={r.type === "document" ? "Document requested, e.g. Form 1099-B" : "Question for the client"} value={r.title} onChange={(e) => set({ creates: q.creates.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)) })} />
                  <Button type="button" variant="ghost" size="icon" aria-label="Remove checklist item" onClick={() => set({ creates: q.creates.filter((_, j) => j !== i) })}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Input placeholder="Instructions for the client (optional)" value={r.instructions ?? ""} onChange={(e) => set({ creates: q.creates.map((x, j) => (j === i ? { ...x, instructions: e.target.value || undefined } : x)) })} />
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>When the client’s answer is</span>
                  {q.type === "yes_no" ? (
                    <select className={cn(select, "h-8 text-xs")} value={String(r.when.value !== false)} onChange={(e) => set({ creates: q.creates.map((x, j) => (j === i ? { ...x, when: { op: "eq", value: e.target.value === "true" } } : x)) })}>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : q.type === "choice" ? (
                    <select className={cn(select, "h-8 text-xs")} value={String(r.when.value)} onChange={(e) => set({ creates: q.creates.map((x, j) => (j === i ? { ...x, when: { op: "eq", value: e.target.value } } : x)) })}>
                      {(q.options ?? []).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="font-medium text-foreground">{whenLabel(q, r.when)}</span>
                  )}
                  {(q.type === "count" || q.type === "group") && (
                    <label className="flex items-center gap-1.5">
                      <input type="checkbox" checked={r.perItem} onChange={(e) => set({ creates: q.creates.map((x, j) => (j === i ? { ...x, perItem: e.target.checked } : x)) })} />
                      {q.type === "group" ? <>One item for each entry (use {"{name}"} in the title for its name)</> : <>One item for each (use {"{n}"} in the title for the number)</>}
                    </label>
                  )}
                </div>
              </div>
            ))}
            <Button type="button" size="sm" variant="outline" disabled={q.creates.length >= 5} onClick={() => set({ creates: [...q.creates, { type: "document", title: "", when: defaultWhen(q), perItem: false }] })}>
              <Plus className="h-4 w-4" /> Add checklist item
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

/** The fields each entry of a list asks for (for a W-2: employer, state, wages). */
function GroupFieldsEditor({ q, set }: { q: Question; set: (patch: Partial<Question>) => void }) {
  const fields = q.fields ?? [];
  const setField = (i: number, patch: Partial<GroupField>) => set({ fields: fields.map((f, j) => (j === i ? { ...f, ...patch } : f)) });
  return (
    <div className="space-y-2">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label>Each entry is called</Label>
          <Input value={q.itemLabel ?? ""} placeholder="e.g. W-2, Dependent, Rental property" onChange={(e) => set({ itemLabel: e.target.value || undefined })} />
        </div>
        <div className="space-y-1">
          <Label>Name each entry by</Label>
          <select className={cn(select, "w-full")} value={q.titleField ?? ""} onChange={(e) => set({ titleField: e.target.value || undefined })}>
            <option value="">Its number</option>
            {/* Entry names appear in checklist titles, so protected numbers can't name them. */}
            {fields
              .filter((f) => !(SENSITIVE_TYPES as readonly string[]).includes(f.type))
              .map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label || f.key}
                </option>
              ))}
          </select>
        </div>
      </div>
      <Label>Fields in each entry</Label>
      {fields.map((f, i) => (
        <div key={f.key} className="grid gap-2 rounded-md border border-border p-2 sm:grid-cols-[2fr_1.4fr_auto_auto]">
          <Input aria-label="Field label" value={f.label} onChange={(e) => setField(i, { label: e.target.value })} />
          <select
            aria-label="Field type"
            className={select}
            value={f.type}
            onChange={(e) => {
              const type = e.target.value as GroupField["type"];
              const options = type === "choice" ? (f.options?.length ? f.options : [{ value: "option_1", label: "Option 1" }, { value: "option_2", label: "Option 2" }]) : undefined;
              // A protected number can't name the entries.
              const unnamed = q.titleField === f.key && (SENSITIVE_TYPES as readonly string[]).includes(type);
              set({ fields: fields.map((x, j) => (j === i ? { ...x, type, options } : x)), ...(unnamed ? { titleField: undefined } : {}) });
            }}
          >
            {FIELD_TYPES.map((t) => (
              <option key={t} value={t}>
                {FIELD_LABELS[t]}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={f.required} onChange={(e) => setField(i, { required: e.target.checked })} /> Required
          </label>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Remove field"
            disabled={fields.length <= 1}
            onClick={() => set({ fields: fields.filter((_, j) => j !== i), titleField: q.titleField === f.key ? undefined : q.titleField })}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          {f.type === "choice" && (
            <Input
              className="sm:col-span-4"
              aria-label="Choices, separated by commas"
              placeholder="Choices, separated by commas"
              value={(f.options ?? []).map((o) => o.label).join(", ")}
              onChange={(e) => {
                const labels = e.target.value.split(",").map((x) => x.trim()).filter(Boolean);
                const taken = new Set<string>();
                setField(i, { options: labels.map((label) => ({ label, value: makeKey(label, taken, "option") })).map((o) => (taken.add(o.value), o)) });
              }}
            />
          )}
        </div>
      ))}
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={fields.length >= 15}
        onClick={() => set({ fields: [...fields, { key: makeKey(`field ${fields.length + 1}`, new Set(fields.map((f) => f.key)), "field"), type: "text", label: "", required: false }] })}
      >
        <Plus className="h-4 w-4" /> Add field
      </Button>
    </div>
  );
}

const blankQuestion = (key: string): Question => ({ key, type: "yes_no", label: "", required: true, prefill: false, showIf: [], creates: [] });

/** Edits `template`, or creates a new form (starting from `copyOf` when duplicating). */
export function FormBuilder({ kind, template, copyOf, onDone }: { kind: FormKind; template?: FormTemplate; copyOf?: FormTemplate; onDone: () => void }) {
  const [name, setName] = useState(template?.name ?? (copyOf ? `${copyOf.name} (copy)` : ""));
  const [active, setActive] = useState(template?.isActive ?? true);
  const [def, setDef] = useState<OrganizerDefinition>(
    template?.definition ?? copyOf?.definition ?? { sections: [{ key: "general", title: kind === "intake" ? "Client information" : "General", questions: [blankQuestion("q_1")] }] },
  );
  const [problems, setProblems] = useState<string[]>([]);
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => {
      const body = { name, module: template?.module ?? "tax", kind, definition: def };
      return template ? api.send("PUT", `/api/organizer-templates/${template.id}`, { ...body, isActive: active }) : api.send("POST", "/api/organizer-templates", body);
    },
    onSuccess: onDone,
    onError: (e) => {
      const details = e instanceof ApiError ? (e.details as { formErrors?: string[]; fieldErrors?: Record<string, string[]> } | undefined) : undefined;
      const list = [...(details?.formErrors ?? []), ...Object.values(details?.fieldErrors ?? {}).flat()];
      setProblems(list.length ? list : [errorMessage(e)]);
    },
  });

  const setSection = (i: number, patch: Partial<OrganizerDefinition["sections"][number]>) => setDef({ sections: def.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const moveIn = <T,>(list: T[], i: number, dir: -1 | 1) => {
    const out = [...list];
    [out[i], out[i + dir]] = [out[i + dir]!, out[i]!];
    return out;
  };
  const flat = def.sections.flatMap((s) => s.questions);
  const questionCount = flat.length;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-[2fr_1fr]">
        <div className="space-y-1">
          <Label htmlFor="form-name">{kind === "intake" ? "Intake form name" : "Organizer name"}</Label>
          <Input id="form-name" value={name} placeholder={kind === "intake" ? "e.g. Client intake — Rental property owner" : "e.g. Rental property organizer"} onChange={(e) => setName(e.target.value)} />
        </div>
        {template && (
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Active (available to send)
          </label>
        )}
      </div>
      {template && <p className="text-xs text-muted-foreground">Changes apply to forms sent from now on. Forms already sent keep the questions the client received.</p>}

      {def.sections.map((sec, si) => {
        const before = def.sections.slice(0, si).flatMap((s) => s.questions);
        return (
          <div key={sec.key} className="rounded-lg border border-card-border bg-card p-4">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">Section {si + 1}</span>
              <Input className="flex-1 font-medium" value={sec.title} placeholder="Section title" onChange={(e) => setSection(si, { title: e.target.value })} />
              <Button type="button" size="icon" variant="ghost" aria-label="Move section up" disabled={si === 0} onClick={() => setDef({ sections: moveIn(def.sections, si, -1) })}>
                <ArrowUp className="h-4 w-4" />
              </Button>
              <Button type="button" size="icon" variant="ghost" aria-label="Move section down" disabled={si === def.sections.length - 1} onClick={() => setDef({ sections: moveIn(def.sections, si, 1) })}>
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label="Delete section"
                disabled={def.sections.length === 1}
                onClick={async () => (await ask({ title: `Delete the section “${sec.title}”?`, description: "Its questions are deleted too.", confirmLabel: "Delete section", destructive: true })) && setDef({ sections: def.sections.filter((_, j) => j !== si) })}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            <ul className="space-y-2">
              {sec.questions.map((q, qi) => (
                <QuestionEditor
                  kind={kind}
                  key={q.key}
                  q={q}
                  earlier={[...before, ...sec.questions.slice(0, qi)]}
                  canMoveUp={qi > 0}
                  canMoveDown={qi < sec.questions.length - 1}
                  onChange={(nq) => setSection(si, { questions: sec.questions.map((x, j) => (j === qi ? nq : x)) })}
                  onMove={(dir) => setSection(si, { questions: moveIn(sec.questions, qi, dir) })}
                  onDelete={() => {
                    if (sec.questions.length === 1) return void setDef({ sections: def.sections.filter((_, j) => j !== si).length ? def.sections.filter((_, j) => j !== si) : def.sections });
                    setSection(si, { questions: sec.questions.filter((_, j) => j !== qi) });
                  }}
                />
              ))}
            </ul>
            <Button type="button" size="sm" variant="outline" className="mt-3" onClick={() => setSection(si, { questions: [...sec.questions, blankQuestion(makeKey(`q ${questionCount + 1}`, allKeys(def)))] })}>
              <Plus className="h-4 w-4" /> Add question
            </Button>
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          const keys = allKeys(def);
          const sk = makeKey(`section ${def.sections.length + 1}`, keys, "section");
          keys.add(sk);
          setDef({ sections: [...def.sections, { key: sk, title: "New section", questions: [blankQuestion(makeKey(`q ${questionCount + 1}`, keys))] }] });
        }}
      >
        <Plus className="h-4 w-4" /> Add section
      </Button>

      {problems.length > 0 && (
        <div role="alert" className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          <p className="font-medium">The form could not be saved:</p>
          <ul className="mt-1 list-disc pl-5">
            {problems.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex flex-wrap gap-2 border-t border-border pt-4">
        <Button
          onClick={() => {
            const empty = flat.filter((q) => !q.label.trim()).length;
            const untitled = flat.flatMap((q) => q.creates).filter((r) => !r.title.trim()).length;
            const local = [
              ...(name.trim() ? [] : ["Enter a name for the form."]),
              ...(empty ? [`${empty} question${empty > 1 ? "s have" : " has"} no wording.`] : []),
              ...(untitled ? [`${untitled} checklist item${untitled > 1 ? "s have" : " has"} no title.`] : []),
            ];
            setProblems(local);
            if (!local.length) save.mutate();
          }}
          disabled={save.isPending}
        >
          {template ? "Save changes" : "Create form"}
        </Button>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <span className="self-center text-xs text-muted-foreground">
          {questionCount} question{questionCount === 1 ? "" : "s"} in {def.sections.length} section{def.sections.length === 1 ? "" : "s"}
        </span>
      </div>
    </div>
  );
}

/** A firm's forms of one kind, with create, edit, duplicate and activate. */
export function FormLibrary({ kind, me }: { kind: FormKind; me: Me }) {
  const { data, isLoading } = useQuery<FormTemplate[]>({ queryKey: ["/api/organizer-templates"] });
  const [editing, setEditing] = useState<FormTemplate | "new" | null>(null);
  const [copyOf, setCopyOf] = useState<FormTemplate | null>(null);
  const canManage = hasPermission(me, "templates.manage");
  const [preview, setPreview] = useState<FormTemplate | null>(null);
  const { toast } = useToast();
  const updateCopies = useMutation({
    meta: { inlineError: true },
    mutationFn: (id: string) => api.send<{ updated: number }>("POST", `/api/organizer-templates/${id}/update-open`),
    onSuccess: (r) => toast({ title: `${r.updated} unfinished cop${r.updated === 1 ? "y" : "ies"} updated` }),
  });
  const [search, setSearch] = useState("");
  const forms = (data ?? []).filter((t) => (t.kind ?? "organizer") === kind);
  const noun = kind === "intake" ? "intake form" : "organizer";
  const shown = forms.filter((t) => matches(search, t.name));

  if (editing) {
    return (
      <Section title={editing === "new" ? (copyOf ? `New ${noun} from “${copyOf.name}”` : `New ${noun}`) : `Edit ${noun}: ${editing.name}`}>
        <FormBuilder
          key={editing === "new" ? `new-${copyOf?.id ?? "blank"}` : editing.id}
          kind={kind}
          template={editing === "new" ? undefined : editing}
          copyOf={editing === "new" ? (copyOf ?? undefined) : undefined}
          onDone={() => {
            setEditing(null);
            setCopyOf(null);
          }}
        />
      </Section>
    );
  }

  return (
    <Section
      title={kind === "intake" ? "Intake forms" : "Organizer templates"}
      description={kind === "intake" ? "Questionnaires sent to new clients during onboarding." : "Questionnaires sent to clients each tax season."}
      action={
        canManage && (
          <Button size="sm" onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4" /> New {noun}
          </Button>
        )
      }
    >
      {isLoading ? (
        <Loading />
      ) : !forms.length ? (
        <Empty>No {noun}s have been created.</Empty>
      ) : (
        <div className="space-y-3">
          {forms.length > 8 && <ListSearch value={search} onChange={setSearch} placeholder={`Search ${noun}s`} />}
          {!shown.length ? (
            <Empty>No {noun}s match the search.</Empty>
          ) : (
        <div role="list" className="divide-y divide-border rounded-md border border-border [&>button]:mx-4 [&>button]:mb-2.5 [&>button]:mt-0 [&>button]:pt-2.5">
          <ShowMore
            key={search}
            items={shown}
            initial={5}
            noun={`${noun}s`}
            render={(t) => {
            const n = t.definition.sections.reduce((a, s) => a + s.questions.length, 0);
            return (
              <div role="listitem" key={t.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {plural(n, "question")} in {plural(t.definition.sections.length, "section")}
                  </p>
                </div>
                <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", t.isActive ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200" : "bg-muted text-muted-foreground")}>
                  {t.isActive ? "Active" : "Inactive"}
                </span>
                <Button size="sm" variant="ghost" onClick={() => setPreview(t)}>
                  <Eye className="h-4 w-4" /> Preview
                </Button>
                {canManage && t.openCopies > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Copies already sent that the client hasn't submitted"
                    disabled={updateCopies.isPending}
                    onClick={async () => {
                      if (
                        await ask({
                          title: `Update ${t.openCopies} unfinished cop${t.openCopies > 1 ? "ies" : "y"}?`,
                          description: `Clients who haven't submitted “${t.name}” will see the form as it is now. Their answers to questions that still exist are kept. Submitted forms never change.`,
                          confirmLabel: "Update copies",
                        })
                      )
                        updateCopies.mutate(t.id);
                    }}
                  >
                    <RefreshCw className="h-4 w-4" /> Update unfinished copies ({t.openCopies})
                  </Button>
                )}
                {canManage && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setEditing(t)}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setCopyOf(t);
                        setEditing("new");
                      }}
                    >
                      <Copy className="h-4 w-4" /> Duplicate
                    </Button>
                  </>
                )}
              </div>
            );
            }}
          />
        </div>
          )}
        </div>
      )}
      {updateCopies.error && <p className="mt-2 text-sm text-destructive">{errorMessage(updateCopies.error)}</p>}
      {preview && <FormPreview form={preview} onClose={() => setPreview(null)} />}
    </Section>
  );
}

/** The form as the client sees it, with conditional questions appearing as you answer. Nothing is saved. */
function FormPreview({ form, onClose }: { form: FormTemplate; onClose: () => void }) {
  const year = new Date().getFullYear() - (form.kind === "intake" ? 0 : 1);
  const def = form.kind === "intake" ? form.definition : forTaxYear(form.definition, year);
  const [answers, setAnswers] = useState<Answers>({});
  const shown = new Set(visibleQuestions(def, answers).map((q) => q.key));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preview: {form.name}</DialogTitle>
          <DialogDescription>
            What the client sees{form.kind === "intake" ? "" : ` for tax year ${year}`}. Answer to see follow-up questions appear. Nothing here is saved or sent.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {def.sections.map((sec) => {
            const qs = sec.questions.filter((q) => shown.has(q.key));
            if (!qs.length) return null;
            return (
              <section key={sec.key} className="rounded-lg border border-card-border">
                <h3 className="border-b border-border bg-secondary/50 px-4 py-2 text-sm font-semibold">{sec.title}</h3>
                <div className="space-y-4 p-4">
                  {qs.map((q) => (
                    <div key={q.key} className="space-y-1.5">
                      <Label htmlFor={`q-${q.key}`} id={`q-${q.key}-label`} className="text-sm font-medium">
                        {fillYear(q.label, year)}
                        {q.required && <span className="text-destructive"> *</span>}
                      </Label>
                      {q.help && <p className="text-xs text-muted-foreground">{fillYear(q.help, year)}</p>}
                      <QuestionInput
                        q={q}
                        year={year}
                        value={answers[q.key]}
                        onChange={(v) =>
                          setAnswers((prev) => {
                            const next = { ...prev };
                            if (v === undefined) delete next[q.key];
                            else next[q.key] = v;
                            return next;
                          })
                        }
                        onInvalid={() => {}}
                      />
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
